import { SYSTEM_ACCOUNT_IDS } from '@shared/constants';
import { match } from 'ts-pattern';
import type { FieldType, FilterOperator, FilterValue, OperatorsByFieldType, PrismaWhereClause } from './types';

/**
 * Type-safe auto where builder function signature.
 * Used by getAutoWhereBuilder factory to return properly typed builders.
 */
type AutoWhereBuilder<TWhere> = (operator: FilterOperator, value: FilterValue) => PrismaWhereClause<TWhere> | null;

/**
 * Auto Where Builders
 *
 * This module provides type-safe automatic where clause builders for different field types.
 * These builders generate Prisma-compatible where conditions based on field type, operator, and value.
 *
 * @module auto-where-builders
 */

/**
 * Generate automatic where clause for bigint fields
 *
 * Handles bigint comparisons with proper null handling and array support for between operator.
 *
 * @param field - The database field name to filter on
 * @param operator - The comparison operator
 * @param value - The bigint value(s) to filter by
 * @returns Prisma where clause or null if invalid
 *
 * @example
 * ```typescript
 * // Simple equality
 * buildBigintWhere('userId', 'equals', 123n);
 * // Returns: { userId: 123n }
 *
 * // Range query
 * buildBigintWhere('amount', 'between', [100n, 1000n]);
 * // Returns: { amount: { gte: 100n, lte: 1000n } }
 *
 * // Null check
 * buildBigintWhere('deletedBy', 'isNull', null);
 * // Returns: { deletedBy: null }
 * ```
 */
export function buildBigintWhere<TWhere>(
    field: keyof TWhere,
    operator: OperatorsByFieldType<'bigint'>,
    value: bigint | null | (bigint | null)[],
): PrismaWhereClause<TWhere> | null {
    if (value === null || value === undefined) {
        if (operator === 'isNull' || operator === 'isNotNull') {
            // Handle null operators
        } else {
            return null;
        }
    }

    return match(operator)
        .with('equals', () => ({ [field]: value }) as Partial<TWhere>)
        .with('not', () => ({ NOT: { [field]: value } as Partial<TWhere> }))
        .with('gt', () => ({ [field]: { gt: value } }) as Partial<TWhere>)
        .with('gte', () => ({ [field]: { gte: value } }) as Partial<TWhere>)
        .with('lt', () => ({ [field]: { lt: value } }) as Partial<TWhere>)
        .with('lte', () => ({ [field]: { lte: value } }) as Partial<TWhere>)
        .with('between', () => {
            if (Array.isArray(value) && value.length === 2) {
                return { [field]: { gte: value[0], lte: value[1] } } as Partial<TWhere>;
            }
            return null;
        })
        .with('isNull', () => ({ [field]: null }) as Partial<TWhere>)
        .with('isNotNull', () => ({ [field]: { not: null } }) as Partial<TWhere>)
        .otherwise(() => null);
}

/**
 * Generate automatic where clause for number fields
 *
 * Supports all numeric comparison operators including ranges.
 *
 * @param field - The database field name to filter on
 * @param operator - The comparison operator
 * @param value - The number value(s) to filter by
 * @returns Prisma where clause or null if invalid
 *
 * @example
 * ```typescript
 * // Greater than
 * buildNumberWhere('age', 'gt', 18);
 * // Returns: { age: { gt: 18 } }
 *
 * // Between range
 * buildNumberWhere('price', 'between', [10, 100]);
 * // Returns: { price: { gte: 10, lte: 100 } }
 * ```
 */
export function buildNumberWhere<TWhere>(
    field: keyof TWhere,
    operator: OperatorsByFieldType<'number'>,
    value: number | number[],
): PrismaWhereClause<TWhere> | null {
    return match(operator)
        .with('equals', () => ({ [field]: value }) as Partial<TWhere>)
        .with('not', () => ({ NOT: { [field]: value } as Partial<TWhere> }))
        .with('gt', () => ({ [field]: { gt: value } }) as Partial<TWhere>)
        .with('gte', () => ({ [field]: { gte: value } }) as Partial<TWhere>)
        .with('lt', () => ({ [field]: { lt: value } }) as Partial<TWhere>)
        .with('lte', () => ({ [field]: { lte: value } }) as Partial<TWhere>)
        .with('between', () => {
            if (Array.isArray(value) && value.length === 2) {
                return { [field]: { gte: value[0], lte: value[1] } } as Partial<TWhere>;
            }
            return null;
        })
        .with('isNull', () => ({ [field]: null }) as Partial<TWhere>)
        .with('isNotNull', () => ({ [field]: { not: null } }) as Partial<TWhere>)
        .otherwise(() => null);
}

/**
 * Generate automatic where clause for text fields
 *
 * All text operations are case-insensitive by default.
 * Supports pattern matching with contains, startsWith, and endsWith.
 *
 * @param field - The database field name to filter on
 * @param operator - The text comparison operator
 * @param value - The text value to filter by
 * @returns Prisma where clause or null if invalid
 *
 * @example
 * ```typescript
 * // Case-insensitive contains
 * buildTextWhere('email', 'contains', 'gmail');
 * // Returns: { email: { contains: 'gmail', mode: 'insensitive' } }
 *
 * // Starts with pattern
 * buildTextWhere('name', 'startsWith', 'John');
 * // Returns: { name: { startsWith: 'John', mode: 'insensitive' } }
 * ```
 */
export function buildTextWhere<TWhere>(
    field: keyof TWhere,
    operator: OperatorsByFieldType<'text'>,
    value: string,
): PrismaWhereClause<TWhere> | null {
    return match(operator)
        .with('equals', () => ({ [field]: { equals: value, mode: 'insensitive' } }) as Partial<TWhere>)
        .with('not', () => ({ [field]: { not: { equals: value, mode: 'insensitive' } } }) as Partial<TWhere>)
        .with('contains', () => ({ [field]: { contains: value, mode: 'insensitive' } }) as Partial<TWhere>)
        .with('startsWith', () => ({ [field]: { startsWith: value, mode: 'insensitive' } }) as Partial<TWhere>)
        .with('endsWith', () => ({ [field]: { endsWith: value, mode: 'insensitive' } }) as Partial<TWhere>)
        .with('isNull', () => ({ [field]: null }) as Partial<TWhere>)
        .with('isNotNull', () => ({ [field]: { not: null } }) as Partial<TWhere>)
        .otherwise(() => null);
}

/**
 * Generate automatic where clause for select fields
 *
 * Handles single selection fields with support for in/notIn operations.
 *
 * @param field - The database field name to filter on
 * @param operator - The selection operator
 * @param value - The selected value(s)
 * @returns Prisma where clause or null if invalid
 *
 * @example
 * ```typescript
 * // Single value selection
 * buildSelectWhere('status', 'equals', 'active');
 * // Returns: { status: 'active' }
 *
 * // Multiple values with IN
 * buildSelectWhere('role', 'in', ['admin', 'moderator']);
 * // Returns: { role: { in: ['admin', 'moderator'] } }
 * ```
 */
export function buildSelectWhere<TWhere, TValue = string>(
    field: keyof TWhere,
    operator: OperatorsByFieldType<'select'>,
    value: TValue,
): PrismaWhereClause<TWhere> | null {
    return match(operator)
        .with('equals', () => ({ [field]: value }) as Partial<TWhere>)
        .with('not', () => ({ NOT: { [field]: value } as Partial<TWhere> }))
        .with('in', () => ({ [field]: { in: Array.isArray(value) ? value : [value] } }) as Partial<TWhere>)
        .with('notIn', () => ({ [field]: { notIn: Array.isArray(value) ? value : [value] } }) as Partial<TWhere>)
        .with('isNull', () => ({ [field]: null }) as Partial<TWhere>)
        .with('isNotNull', () => ({ [field]: { not: null } }) as Partial<TWhere>)
        .otherwise(() => null);
}

/**
 * Generate automatic where clause for user/bigint select fields
 *
 * Special handler for user selection fields that use bigint IDs.
 * Properly handles both single and array values with null safety.
 * Converts UNALLOCATED (-9999n) to null for proper database filtering.
 *
 * @param field - The database field name to filter on
 * @param operator - The selection operator
 * @param value - The user ID(s) as bigint
 * @returns Prisma where clause or null if invalid
 *
 * @example
 * ```typescript
 * // Single user
 * buildUserWhere('assignedTo', 'equals', 123n);
 * // Returns: { assignedTo: 123n }
 *
 * // Multiple users
 * buildUserWhere('createdBy', 'in', [123n, 456n, 789n]);
 * // Returns: { createdBy: { in: [123n, 456n, 789n] } }
 *
 * // Unallocated user (converts -9999n to null)
 * buildUserWhere('ownerId', 'equals', -9999n);
 * // Returns: { ownerId: null }
 *
 * // Not assigned
 * buildUserWhere('ownerId', 'isNull', null);
 * // Returns: { ownerId: null }
 * ```
 */
export function buildUserWhere<TWhere>(
    field: keyof TWhere,
    operator: OperatorsByFieldType<'select'>,
    value: bigint | bigint[] | null,
): PrismaWhereClause<TWhere> | null {
    // Helper function to convert UNALLOCATED to null
    const convertUnallocatedToNull = (val: bigint | null): bigint | null => {
        return val === SYSTEM_ACCOUNT_IDS.UNALLOCATED ? null : val;
    };

    // Handle null values
    if (value === null && operator !== 'isNull' && operator !== 'isNotNull') {
        return null;
    }

    return match(operator)
        .with('equals', () => {
            if (Array.isArray(value)) {
                // If array provided for equals, use the first value
                if (value.length > 0) {
                    const convertedValue = convertUnallocatedToNull(value[0]);
                    return { [field]: convertedValue } as Partial<TWhere>;
                }
                return null;
            }
            const convertedValue = convertUnallocatedToNull(value);
            return { [field]: convertedValue } as Partial<TWhere>;
        })
        .with('not', () => {
            if (Array.isArray(value)) {
                // If array provided for not, use the first value
                if (value.length > 0) {
                    const convertedValue = convertUnallocatedToNull(value[0]);
                    return convertedValue === null
                        ? ({ [field]: { not: null } } as Partial<TWhere>)
                        : ({ [field]: { not: convertedValue } } as Partial<TWhere>);
                }
                return null;
            }
            const convertedValue = convertUnallocatedToNull(value);
            return convertedValue === null
                ? ({ [field]: { not: null } } as Partial<TWhere>)
                : ({ [field]: { not: convertedValue } } as Partial<TWhere>);
        })
        .with('in', () => {
            const arrayValue = Array.isArray(value) ? value : value !== null ? [value] : [];

            // Convert UNALLOCATED values to null and separate them
            const convertedValues = arrayValue.map(convertUnallocatedToNull);
            const hasNull = convertedValues.includes(null);
            const nonNullValues = convertedValues.filter((v): v is bigint => v !== null);

            if (hasNull && nonNullValues.length > 0) {
                // If we have both null and non-null values, use OR
                return {
                    OR: [{ [field]: null } as Partial<TWhere>, { [field]: { in: nonNullValues } } as Partial<TWhere>],
                };
            } else if (hasNull) {
                // Only null value
                return { [field]: null } as Partial<TWhere>;
            } else if (nonNullValues.length > 0) {
                // Only non-null values
                return { [field]: { in: nonNullValues } } as Partial<TWhere>;
            }
            return null;
        })
        .with('notIn', () => {
            const arrayValue = Array.isArray(value) ? value : value !== null ? [value] : [];

            // Convert UNALLOCATED values to null and separate them
            const convertedValues = arrayValue.map(convertUnallocatedToNull);
            const hasNull = convertedValues.includes(null);
            const nonNullValues = convertedValues.filter((v): v is bigint => v !== null);

            if (hasNull && nonNullValues.length > 0) {
                // If we have both null and non-null values, use AND
                return {
                    AND: [
                        { [field]: { not: null } } as Partial<TWhere>,
                        { [field]: { notIn: nonNullValues } } as Partial<TWhere>,
                    ],
                };
            } else if (hasNull) {
                // Only null value
                return { [field]: { not: null } } as Partial<TWhere>;
            } else if (nonNullValues.length > 0) {
                // Only non-null values
                return { [field]: { notIn: nonNullValues } } as Partial<TWhere>;
            }
            return null;
        })
        .with('isNull', () => ({ [field]: null }) as Partial<TWhere>)
        .with('isNotNull', () => ({ [field]: { not: null } }) as Partial<TWhere>)
        .otherwise(() => null);
}

/**
 * Generate automatic where clause for multiselect fields
 *
 * Uses Prisma's array field operators (hasSome, isEmpty) for PostgreSQL arrays.
 *
 * @param field - The database field name to filter on
 * @param operator - The multiselect operator
 * @param value - Array of selected values
 * @returns Prisma where clause or null if invalid
 *
 * @example
 * ```typescript
 * // Has any of the specified tags
 * buildMultiselectWhere('tags', 'in', ['urgent', 'important']);
 * // Returns: { tags: { hasSome: ['urgent', 'important'] } }
 *
 * // Empty array check
 * buildMultiselectWhere('categories', 'isNull', []);
 * // Returns: { categories: { isEmpty: true } }
 * ```
 */
export function buildMultiselectWhere<TWhere, TValue = string>(
    field: keyof TWhere,
    operator: OperatorsByFieldType<'multiselect'>,
    value: TValue[],
): PrismaWhereClause<TWhere> | null {
    return match(operator)
        .with('in', () => ({ [field]: { in: value } }) as Partial<TWhere>)
        .with('notIn', () => ({ [field]: { notIn: value } }) as Partial<TWhere>)
        .with('isNull', () => ({ [field]: null }) as Partial<TWhere>)
        .with('isNotNull', () => ({ [field]: { not: null } }) as Partial<TWhere>)
        .otherwise(() => null);
}

/**
 * Generate automatic where clause for boolean fields
 *
 * Simple true/false filtering with null support.
 *
 * @param field - The database field name to filter on
 * @param operator - The boolean operator
 * @param value - The boolean value
 * @returns Prisma where clause or null if invalid
 *
 * @example
 * ```typescript
 * // Is active
 * buildBooleanWhere('isActive', 'equals', true);
 * // Returns: { isActive: true }
 *
 * // Is not verified
 * buildBooleanWhere('isVerified', 'not', true);
 * // Returns: { isVerified: { not: true } }
 * ```
 */
export function buildBooleanWhere<TWhere>(
    field: keyof TWhere,
    operator: OperatorsByFieldType<'boolean'>,
    value: boolean,
): PrismaWhereClause<TWhere> | null {
    return match(operator)
        .with('equals', () => ({ [field]: value }) as Partial<TWhere>)
        .with('not', () => ({ [field]: { not: value } }) as Partial<TWhere>)
        .with('isNull', () => ({ [field]: null }) as Partial<TWhere>)
        .with('isNotNull', () => ({ [field]: { not: null } }) as Partial<TWhere>)
        .otherwise(() => null);
}

/**
 * Generate automatic where clause for date fields
 *
 * Special handling for date equality to match entire day (00:00:00 to 23:59:59).
 * Supports date range queries.
 *
 * @param field - The database field name to filter on
 * @param operator - The date comparison operator
 * @param value - The date value(s)
 * @returns Prisma where clause or null if invalid
 *
 * @example
 * ```typescript
 * // Match entire day
 * buildDateWhere('createdAt', 'equals', new Date('2024-01-01'));
 * // Returns: { createdAt: { gte: '2024-01-01T00:00:00', lte: '2024-01-01T23:59:59' } }
 *
 * // Date range
 * buildDateWhere('dueDate', 'between', [new Date('2024-01-01'), new Date('2024-01-31')]);
 * // Returns: { dueDate: { gte: '2024-01-01', lte: '2024-01-31' } }
 * ```
 */
export function buildDateWhere<TWhere>(
    field: keyof TWhere,
    operator: OperatorsByFieldType<'date'>,
    value: Date | Date[],
): PrismaWhereClause<TWhere> | null {
    return (
        match(operator)
            .with('equals', () => {
                if (Array.isArray(value)) return null; // equals shouldn't receive array
                const startOfDay = new Date(value);
                startOfDay.setHours(0, 0, 0, 0);
                const endOfDay = new Date(value);
                endOfDay.setHours(23, 59, 59, 999);
                return {
                    [field]: {
                        gte: startOfDay,
                        lte: endOfDay,
                    },
                } as Partial<TWhere>;
            })
            .with('not', () => {
                if (Array.isArray(value)) return null; // not shouldn't receive array
                const startOfDay = new Date(value);
                startOfDay.setHours(0, 0, 0, 0);
                const endOfDay = new Date(value);
                endOfDay.setHours(23, 59, 59, 999);
                return {
                    OR: [
                        { [field]: { lt: startOfDay } } as Partial<TWhere>,
                        { [field]: { gt: endOfDay } } as Partial<TWhere>,
                    ],
                };
            })
            .with('gt', () => (Array.isArray(value) ? null : ({ [field]: { gt: value } } as Partial<TWhere>)))
            .with('gte', () => (Array.isArray(value) ? null : ({ [field]: { gte: value } } as Partial<TWhere>)))
            .with('lt', () => (Array.isArray(value) ? null : ({ [field]: { lt: value } } as Partial<TWhere>)))
            .with('lte', () => (Array.isArray(value) ? null : ({ [field]: { lte: value } } as Partial<TWhere>)))
            // `between` and `withinRelative` both arrive as a resolved [start, end] range — the latter's
            // relative token is expanded to a concrete range by the date field's `transform`.
            .with('between', 'withinRelative', () => {
                if (Array.isArray(value) && value.length === 2) {
                    return {
                        [field]: {
                            gte: value[0],
                            lte: value[1],
                        },
                    } as Partial<TWhere>;
                }
                return null;
            })
            .with('isNull', () => ({ [field]: null }) as Partial<TWhere>)
            .with('isNotNull', () => ({ [field]: { not: null } }) as Partial<TWhere>)
            .otherwise(() => null)
    );
}

/**
 * Generate automatic where clause for datetime fields
 *
 * Precise datetime filtering including exact time comparisons.
 *
 * @param field - The database field name to filter on
 * @param operator - The datetime comparison operator
 * @param value - The datetime value(s)
 * @returns Prisma where clause or null if invalid
 *
 * @example
 * ```typescript
 * // After specific time
 * buildDatetimeWhere('lastLogin', 'gt', new Date('2024-01-01T10:30:00'));
 * // Returns: { lastLogin: { gt: '2024-01-01T10:30:00' } }
 *
 * // Time range
 * const start = new Date('2024-01-01T09:00:00');
 * const end = new Date('2024-01-01T17:00:00');
 * buildDatetimeWhere('timestamp', 'between', [start, end]);
 * // Returns: { timestamp: { gte: start, lte: end } }
 * ```
 */
export function buildDatetimeWhere<TWhere>(
    field: keyof TWhere,
    operator: OperatorsByFieldType<'datetime'>,
    value: Date | Date[],
): PrismaWhereClause<TWhere> | null {
    return (
        match(operator)
            .with('equals', () => ({ [field]: value }) as Partial<TWhere>)
            .with('not', () => ({ [field]: { not: value } }) as Partial<TWhere>)
            .with('gt', () => ({ [field]: { gt: value } }) as Partial<TWhere>)
            .with('gte', () => ({ [field]: { gte: value } }) as Partial<TWhere>)
            .with('lt', () => ({ [field]: { lt: value } }) as Partial<TWhere>)
            .with('lte', () => ({ [field]: { lte: value } }) as Partial<TWhere>)
            // `between` and `withinRelative` both arrive as a resolved [start, end] range — the latter's
            // relative token is expanded to a concrete range by the date field's `transform`.
            .with('between', 'withinRelative', () => {
                if (Array.isArray(value) && value.length === 2) {
                    return {
                        [field]: {
                            gte: value[0],
                            lte: value[1],
                        },
                    } as Partial<TWhere>;
                }
                return null;
            })
            .with('isNull', () => ({ [field]: null }) as Partial<TWhere>)
            .with('isNotNull', () => ({ [field]: { not: null } }) as Partial<TWhere>)
            .otherwise(() => null)
    );
}

/**
 * Get the appropriate automatic where builder for a field type
 *
 * Factory function that returns the correct where builder based on field type.
 * Used internally by the filter system to automatically generate where clauses.
 *
 * @param fieldType - The type of field
 * @param field - The database field name
 * @returns The appropriate where builder function or null
 *
 * @example
 * ```typescript
 * // Get builder for a text field
 * const textBuilder = getAutoWhereBuilder('text', 'username');
 * const whereClause = textBuilder('contains', 'john');
 * // Returns: { username: { contains: 'john', mode: 'insensitive' } }
 *
 * // Get builder for a number field
 * const numberBuilder = getAutoWhereBuilder('number', 'age');
 * const whereClause = numberBuilder('gte', 18);
 * // Returns: { age: { gte: 18 } }
 * ```
 */
export function getAutoWhereBuilder<TWhere>(
    fieldType: FieldType,
    field: keyof TWhere,
): AutoWhereBuilder<TWhere> | null {
    switch (fieldType) {
        case 'bigint':
            return (operator, value) =>
                buildBigintWhere<TWhere>(
                    field,
                    operator as OperatorsByFieldType<'bigint'>,
                    value as bigint | null | (bigint | null)[],
                );
        case 'number':
            return (operator, value) =>
                buildNumberWhere<TWhere>(field, operator as OperatorsByFieldType<'number'>, value as number | number[]);
        case 'text':
            return (operator, value) =>
                buildTextWhere<TWhere>(field, operator as OperatorsByFieldType<'text'>, value as string);
        case 'select':
            return (operator, value) =>
                buildSelectWhere<TWhere, FilterValue>(field, operator as OperatorsByFieldType<'select'>, value);
        case 'multiselect':
            return (operator, value) =>
                buildMultiselectWhere<TWhere>(
                    field,
                    operator as OperatorsByFieldType<'multiselect'>,
                    value as string[],
                );
        case 'boolean':
            return (operator, value) =>
                buildBooleanWhere<TWhere>(field, operator as OperatorsByFieldType<'boolean'>, value as boolean);
        case 'date':
            return (operator, value) =>
                buildDateWhere<TWhere>(field, operator as OperatorsByFieldType<'date'>, value as Date | Date[]);
        case 'datetime':
            return (operator, value) =>
                buildDatetimeWhere<TWhere>(field, operator as OperatorsByFieldType<'datetime'>, value as Date | Date[]);
        default:
            return null;
    }
}
